/**
 * Map Proclaim-shaped snapshots → AudioFrameInput.
 * Structural types only — no import from prototypes (keeps shared free of Game).
 */

import type { AudioFrameInput, AudioMissionStatus } from "./frame";

export type ProclaimBoardSlot = {
  side: "player" | "enemy" | string;
  level?: number;
  verbs?: { contest?: number };
} | null;

export type ProclaimBoardLayer = {
  tilt: number;
  slots: readonly ProclaimBoardSlot[];
};

/** Minimal snapshot shape used by audio (compatible with GameSnapshot). */
export type ProclaimAudioSnapshot = {
  status: AudioMissionStatus;
  resources: {
    integrity: number;
    maxIntegrity: number;
  };
  board: {
    mind: ProclaimBoardLayer;
    heart: ProclaimBoardLayer;
    will: ProclaimBoardLayer;
  };
  proclaimLock: number;
  proclaimLockMax: number;
  paused: boolean;
};

/**
 * Mutual contest intensity 0–1 when enemy + player share a layer.
 * Weighted by contest verbs / levels when present.
 */
export function deriveContestIntensity(
  board: ProclaimAudioSnapshot["board"],
): number {
  let sum = 0;
  for (const id of ["mind", "heart", "will"] as const) {
    let enemyContest = 0;
    let playerContest = 0;
    let enemies = 0;
    let players = 0;
    for (const p of board[id].slots) {
      if (!p) continue;
      const c = p.verbs?.contest ?? 0;
      const lv = Math.max(1, p.level ?? 1);
      if (p.side === "enemy") {
        enemies++;
        enemyContest = Math.max(enemyContest, c * (0.6 + lv * 0.15));
      } else if (p.side === "player") {
        players++;
        playerContest = Math.max(playerContest, c * (0.6 + lv * 0.15));
      }
    }
    if (enemies > 0 && players > 0) {
      // Presence of both sides is enough; verbs deepen the grind
      const base = 0.22 + Math.min(0.35, (enemies + players - 2) * 0.06);
      const verb = Math.min(0.45, (enemyContest + playerContest) * 0.12);
      sum += base + verb;
    }
  }
  return Math.max(0, Math.min(1, sum));
}

/**
 * Derive continuous audio frame from a Proclaim snapshot.
 * Omits spirit/strain (P3 zeroes them every frame).
 */
export function audioFrameFromProclaimSnapshot(
  snap: ProclaimAudioSnapshot,
): AudioFrameInput {
  const board = snap.board;
  let lieCount = 0;
  let willEnemyCount = 0;
  let willPlayerCount = 0;

  for (const id of ["mind", "heart", "will"] as const) {
    for (const p of board[id].slots) {
      if (!p) continue;
      if (p.side === "enemy") {
        lieCount++;
        if (id === "will") willEnemyCount++;
      } else if (p.side === "player" && id === "will") {
        willPlayerCount++;
      }
    }
  }

  return {
    integrity: snap.resources.integrity,
    maxIntegrity: snap.resources.maxIntegrity,
    tilts: {
      mind: board.mind.tilt,
      heart: board.heart.tilt,
      will: board.will.tilt,
    },
    proclaimLock: snap.proclaimLock,
    proclaimLockMax: snap.proclaimLockMax,
    lieCount,
    willEnemyCount,
    willHeld: willPlayerCount > 0,
    paused: snap.paused,
    status: snap.status,
    contestIntensity: deriveContestIntensity(board),
  };
}
